"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.measureValueFilterOperator = exports.measureValueFilterCondition = exports.measureValueFilterMeasure = exports.relativeDateFilterValues = exports.absoluteDateFilterValues = exports.filterMeasureRef = exports.filterObjRef = exports.filterAttributeElements = exports.attributeElementsCount = exports.attributeElementsIsEmpty = exports.filterIsEmpty = exports.isAttributeElementsByValue = exports.isAttributeElementsByRef = exports.isRangeConditionOperator = exports.isRangeCondition = exports.isComparisonConditionOperator = exports.isComparisonCondition = exports.isFilter = exports.isRankingFilter = exports.isMeasureValueFilter = exports.isDateFilter = exports.isAttributeFilter = exports.isAllTimeDateFilter = exports.isRelativeDateFilter = exports.isAbsoluteDateFilter = exports.isNegativeAttributeFilter = exports.isPositiveAttributeFilter = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2021 GoodData Corporation
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
//
// Type guards
//
/**
 * Type guard checking whether the provided object is a positive attribute filter.
 *
 * @public
 */
function isPositiveAttributeFilter(obj) {
    return !isEmpty_1.default(obj) && obj.positiveAttributeFilter !== undefined;
}
exports.isPositiveAttributeFilter = isPositiveAttributeFilter;
/**
 * Type guard checking whether the provided object is a negative attribute filter.
 *
 * @public
 */
function isNegativeAttributeFilter(obj) {
    return !isEmpty_1.default(obj) && obj.negativeAttributeFilter !== undefined;
}
exports.isNegativeAttributeFilter = isNegativeAttributeFilter;
/**
 * Type guard checking whether the provided object is an absolute date filter.
 *
 * @public
 */
function isAbsoluteDateFilter(obj) {
    return !isEmpty_1.default(obj) && obj.absoluteDateFilter !== undefined;
}
exports.isAbsoluteDateFilter = isAbsoluteDateFilter;
/**
 * Type guard checking whether the provided object is a relative date filter.
 *
 * @public
 */
function isRelativeDateFilter(obj) {
    return !isEmpty_1.default(obj) && obj.relativeDateFilter !== undefined;
}
exports.isRelativeDateFilter = isRelativeDateFilter;
/**
 * Type guard checking whether the provided object is an all time date filter.
 *
 * @public
 */
function isAllTimeDateFilter(obj) {
    var _a;
    return (!isEmpty_1.default(obj) &&
        ((_a = obj.relativeDateFilter) === null || _a === void 0 ? void 0 : _a.granularity) === "ALL_TIME_GRANULARITY");
}
exports.isAllTimeDateFilter = isAllTimeDateFilter;
/**
 * Type guard checking whether the provided object is an attribute filter.
 *
 * @public
 */
function isAttributeFilter(obj) {
    return isPositiveAttributeFilter(obj) || isNegativeAttributeFilter(obj);
}
exports.isAttributeFilter = isAttributeFilter;
/**
 * Type guard checking whether the provided object is a date filter.
 *
 * @public
 */
function isDateFilter(obj) {
    return isRelativeDateFilter(obj) || isAbsoluteDateFilter(obj);
}
exports.isDateFilter = isDateFilter;
/**
 * Type guard checking whether the provided object is a measure value filter.
 *
 * @public
 */
function isMeasureValueFilter(obj) {
    return !isEmpty_1.default(obj) && obj.measureValueFilter !== undefined;
}
exports.isMeasureValueFilter = isMeasureValueFilter;
/**
 * Type guard checking whether the provided object is a ranking filter.
 *
 * @public
 */
function isRankingFilter(obj) {
    return !isEmpty_1.default(obj) && obj.rankingFilter !== undefined;
}
exports.isRankingFilter = isRankingFilter;
/**
 * Type guard checking whether the provided object is a filter.
 *
 * @public
 */
function isFilter(obj) {
    return isDateFilter(obj) || isAttributeFilter(obj) || isMeasureValueFilter(obj) || isRankingFilter(obj);
}
exports.isFilter = isFilter;
/**
 * Type guard checking whether the provided object is a measure value filter's comparison condition.
 *
 * @public
 */
function isComparisonCondition(obj) {
    return !isEmpty_1.default(obj) && obj.comparison !== undefined;
}
exports.isComparisonCondition = isComparisonCondition;
/**
 * Type guard checking whether the provided operator is a measure value filter's comparison operator.
 *
 * @public
 */
function isComparisonConditionOperator(obj) {
    return (obj === "GREATER_THAN" ||
        obj === "GREATER_THAN_OR_EQUAL_TO" ||
        obj === "LESS_THAN" ||
        obj === "LESS_THAN_OR_EQUAL_TO" ||
        obj === "EQUAL_TO" ||
        obj === "NOT_EQUAL_TO");
}
exports.isComparisonConditionOperator = isComparisonConditionOperator;
/**
 * Type guard checking whether the provided object is a measure value filter's range condition.
 *
 * @public
 */
function isRangeCondition(obj) {
    return !isEmpty_1.default(obj) && obj.range !== undefined;
}
exports.isRangeCondition = isRangeCondition;
/**
 * Type guard checking whether the provided object is a measure value filter's range condition operator.
 *
 * @public
 */
function isRangeConditionOperator(obj) {
    return obj === "BETWEEN" || obj === "NOT_BETWEEN";
}
exports.isRangeConditionOperator = isRangeConditionOperator;
/**
 * Type guard checking whether the provided object is list of attribute elements specified by URI reference.
 *
 * @public
 */
function isAttributeElementsByRef(obj) {
    return !isEmpty_1.default(obj) && obj.uris !== undefined;
}
exports.isAttributeElementsByRef = isAttributeElementsByRef;
/**
 * Type guard checking whether the provided object is list of attribute elements specified by their text value.
 *
 * @public
 */
function isAttributeElementsByValue(obj) {
    return !isEmpty_1.default(obj) && obj.values !== undefined;
}
exports.isAttributeElementsByValue = isAttributeElementsByValue;
//
// Functions
//
/**
 * Tests whether the provided attribute element does not specify any attribute elements.
 *
 * @param filter - attribute filter to test
 * @returns true if empty = no attribute elements
 * @public
 */
function filterIsEmpty(filter) {
    ts_invariant_1.default(filter, "filter must be specified");
    if (isPositiveAttributeFilter(filter)) {
        return attributeElementsIsEmpty(filter.positiveAttributeFilter.in);
    }
    return attributeElementsIsEmpty(filter.negativeAttributeFilter.notIn);
}
exports.filterIsEmpty = filterIsEmpty;
/**
 * Tests whether the attribute elements object is empty.
 *
 * @param attributeElements - object to test
 * @returns true if empty = attribute elements not specified in any way (URI or value)
 * @internal
 */
function attributeElementsIsEmpty(attributeElements) {
    ts_invariant_1.default(attributeElements, "attribute elements must be specified");
    if (isAttributeElementsByRef(attributeElements)) {
        return isEmpty_1.default(attributeElements.uris);
    }
    return isEmpty_1.default(attributeElements.values);
}
exports.attributeElementsIsEmpty = attributeElementsIsEmpty;
/**
 * Gets the number of items in the {@link IAttributeElements}.
 *
 * @param attributeElements - object to test
 * @returns the number of items
 * @internal
 */
function attributeElementsCount(attributeElements) {
    ts_invariant_1.default(attributeElements, "attribute elements must be specified");
    if (isAttributeElementsByRef(attributeElements)) {
        return attributeElements.uris.length;
    }
    return attributeElements.values.length;
}
exports.attributeElementsCount = attributeElementsCount;
function filterAttributeElements(filter) {
    ts_invariant_1.default(filter, "attribute elements must be specified");
    if (!isAttributeFilter(filter)) {
        return undefined;
    }
    return isPositiveAttributeFilter(filter)
        ? filter.positiveAttributeFilter.in
        : filter.negativeAttributeFilter.notIn;
}
exports.filterAttributeElements = filterAttributeElements;
function filterObjRef(filter) {
    ts_invariant_1.default(filter, "filter must be specified");
    if (isPositiveAttributeFilter(filter)) {
        return filter.positiveAttributeFilter.displayForm;
    }
    if (isNegativeAttributeFilter(filter)) {
        return filter.negativeAttributeFilter.displayForm;
    }
    if (isAbsoluteDateFilter(filter)) {
        return filter.absoluteDateFilter.dataSet;
    }
    if (isRelativeDateFilter(filter)) {
        return filter.relativeDateFilter.dataSet;
    }
    return undefined;
}
exports.filterObjRef = filterObjRef;
/**
 * Gets reference to a measure being used for filtering if the provided filter is measure based. For other filters return undefined.
 *
 * @public
 */
function filterMeasureRef(filter) {
    return isRankingFilter(filter)
        ? filter.rankingFilter.measure
        : isMeasureValueFilter(filter)
            ? filter.measureValueFilter.measure
            : undefined;
}
exports.filterMeasureRef = filterMeasureRef;
/**
 * Gets effective values of an absolute date filter.
 *
 * @param filter - date filter to work with
 * @returns filter values
 * @public
 */
function absoluteDateFilterValues(filter) {
    ts_invariant_1.default(filter, "filter must be specified");
    return {
        from: filter.absoluteDateFilter.from,
        to: filter.absoluteDateFilter.to,
    };
}
exports.absoluteDateFilterValues = absoluteDateFilterValues;
/**
 * Gets effective values of a relative date filter.
 *
 * @param filter - date filter to work with
 * @returns filter values
 * @public
 */
function relativeDateFilterValues(filter) {
    ts_invariant_1.default(filter, "filter must be specified");
    return {
        from: filter.relativeDateFilter.from,
        to: filter.relativeDateFilter.to,
        granularity: filter.relativeDateFilter.granularity,
    };
}
exports.relativeDateFilterValues = relativeDateFilterValues;
/**
 * Gets measure value filter measure.
 * @param filter - measure value filter to work with
 * @returns filter measure
 * @public
 */
function measureValueFilterMeasure(filter) {
    ts_invariant_1.default(filter, "filter must be specified");
    return filter.measureValueFilter.measure;
}
exports.measureValueFilterMeasure = measureValueFilterMeasure;
/**
 * Gets measure value filter condition.
 * @param filter - measure value filter to work with
 * @returns filter condition
 * @public
 */
function measureValueFilterCondition(filter) {
    ts_invariant_1.default(filter, "filter must be specified");
    return filter.measureValueFilter.condition;
}
exports.measureValueFilterCondition = measureValueFilterCondition;
/**
 * Gets operator used in measure value filter condition.
 *
 * @param filter - filter to get operator from
 * @returns undefined if no condition in the filter
 * @public
 */
function measureValueFilterOperator(filter) {
    ts_invariant_1.default(filter, "filter must be specified");
    if (isComparisonCondition(filter.measureValueFilter.condition)) {
        return filter.measureValueFilter.condition.comparison.operator;
    }
    else if (isRangeCondition(filter.measureValueFilter.condition)) {
        return filter.measureValueFilter.condition.range.operator;
    }
    return undefined;
}
exports.measureValueFilterOperator = measureValueFilterOperator;
//# sourceMappingURL=index.js.map